import { jsPDF } from "jspdf";
import QRCode from "qrcode";

// Bilingual labels shared by the admin panel and the PDF (Whop reads proofs in English).
export const PROOF_L = {
  fr: {
    title: "Justificatif de livraison",
    subtitle: "Proof of Delivery",
    docNo: "N° de justificatif",
    generated: "Document généré le",
    payment: "Paiement",
    name: "Nom",
    email: "E-mail du client",
    plan: "Offre achetée",
    amount: "Montant",
    paidAt: "Date et heure du paiement",
    payId: "ID de paiement Whop",
    memId: "ID de membership Whop",
    method: "Moyen de paiement",
    status: "Statut du paiement",
    account: "Compte marchand",
    consent: "Consentement au checkout",
    cgv: "Conditions générales de vente (CGV)",
    waiver: "Renonciation au droit de rétractation",
    checked: "Cochée",
    notChecked: "Non cochée",
    at: "Le",
    text: "Texte exact",
    ip: "Adresse IP",
    device: "Appareil / navigateur",
    cgvLink: "Lien vers les CGV",
    access: "Accès livré",
    accountCreated: "Création du compte",
    firstAccess: "Premier accès à l'espace",
    firstBienvenue: "Arrivée sur /bienvenue (après paiement)",
    delay: "Délai paiement → accès",
    deliveredIn: "accès livré {n} secondes après le paiement",
    usage: "Utilisation",
    logins: "Historique des connexions",
    loginCount: "Nombre total de connexions",
    timeSpent: "Temps passé dans l'espace",
    businesses: "Business générés",
    regens: "Nombre de régénérations",
    coach: "Messages envoyés au coach IA",
    missions: "Missions",
    missionsDone: "terminées",
    missionsTotal: "au total",
    preview: "Aperçu du contenu livré",
    na: "Non disponible",
    download: "Télécharger en PDF",
    register: "création",
    seconds: "s",
    minutes: "min",
    integrity: "Empreinte d'intégrité (SHA-256)",
    verify: "Vérifier l'authenticité de ce justificatif",
    scan: "Scanner pour vérifier",
    welcomeEmail: "Email de bienvenue",
    sentAt: "Envoyé le",
    sentTo: "Destinataire",
    sentStatus: "Statut d'envoi",
    resend: "Renvoyer l'email de bienvenue",
  },
  en: {
    title: "Proof of Delivery",
    subtitle: "Justificatif de livraison",
    docNo: "Proof number",
    generated: "Document generated on",
    payment: "Payment",
    name: "Name",
    email: "Customer e-mail",
    plan: "Plan purchased",
    amount: "Amount",
    paidAt: "Payment date & time",
    payId: "Whop payment ID",
    memId: "Whop membership ID",
    method: "Payment method",
    status: "Payment status",
    account: "Merchant account",
    consent: "Checkout consent",
    cgv: "Terms of Sale (CGV)",
    waiver: "Waiver of the right of withdrawal",
    checked: "Checked",
    notChecked: "Not checked",
    at: "At",
    text: "Exact wording",
    ip: "IP address",
    device: "Device / browser",
    cgvLink: "Link to Terms of Sale",
    access: "Access delivered",
    accountCreated: "Account creation",
    firstAccess: "First access to the space",
    firstBienvenue: "Arrival on /bienvenue (after payment)",
    delay: "Delay payment → access",
    deliveredIn: "access delivered {n} seconds after payment",
    usage: "Usage",
    logins: "Login history",
    loginCount: "Total logins",
    timeSpent: "Time spent in the space",
    businesses: "Generated businesses",
    regens: "Number of regenerations",
    coach: "Messages sent to the AI coach",
    missions: "Missions",
    missionsDone: "completed",
    missionsTotal: "total",
    preview: "Delivered content preview",
    na: "Not available",
    download: "Download PDF",
    register: "signup",
    seconds: "s",
    minutes: "min",
    integrity: "Integrity fingerprint (SHA-256)",
    verify: "Verify the authenticity of this proof",
    scan: "Scan to verify",
    welcomeEmail: "Welcome email",
    sentAt: "Sent at",
    sentTo: "Recipient",
    sentStatus: "Delivery status",
    resend: "Resend welcome email",
  },
};

export const CGV_URL = "https://idevorai.com/cgv";

const pad = (n) => String(n).padStart(2, "0");
export const fmtUTC = (iso) => {
  if (!iso) return null;
  const d = new Date(iso);
  if (isNaN(d)) return null;
  return `${d.getUTCFullYear()}-${pad(d.getUTCMonth() + 1)}-${pad(d.getUTCDate())} ${pad(d.getUTCHours())}:${pad(d.getUTCMinutes())}:${pad(d.getUTCSeconds())} UTC`;
};
export const fmtBrussels = (iso) => {
  if (!iso) return null;
  const d = new Date(iso);
  if (isNaN(d)) return null;
  return new Date(iso).toLocaleString("fr-FR", { timeZone: "Europe/Brussels", year: "numeric", month: "2-digit", day: "2-digit", hour: "2-digit", minute: "2-digit", second: "2-digit" }) + " (Bruxelles)";
};
export const fmtDual = (iso, na) => {
  const u = fmtUTC(iso);
  if (!u) return na;
  return `${u}  ·  ${fmtBrussels(iso)}`;
};
export const fmtDuration = (sec, L) => {
  if (sec == null) return L.na;
  if (sec < 60) return `${sec} ${L.seconds}`;
  const m = Math.floor(sec / 60);
  const s = sec % 60;
  return `${m} ${L.minutes} ${s ? s + L.seconds : ""}`.trim();
};

export async function downloadProofPDF(data, lang, verifyUrl) {
  const L = PROOF_L[lang] || PROOF_L.fr;
  const doc = new jsPDF({ unit: "mm", format: "a4" });
  let qrDataUrl = null;
  if (verifyUrl) {
    try { qrDataUrl = await QRCode.toDataURL(verifyUrl, { margin: 1, width: 240 }); } catch (e) { qrDataUrl = null; }
  }
  const W = 210, M = 16;
  const INK = [17, 17, 17], MUTE = [110, 110, 110], ACCENT = [124, 58, 237];
  let y = 18;

  const ensure = (h) => { if (y + h > 285) { doc.addPage(); y = 18; } };
  const sectionTitle = (t) => {
    ensure(12);
    y += 2;
    doc.setFont("helvetica", "bold"); doc.setFontSize(11); doc.setTextColor(...ACCENT);
    doc.text(t.toUpperCase(), M, y);
    doc.setDrawColor(230, 230, 230); doc.line(M, y + 1.5, W - M, y + 1.5);
    y += 7;
  };
  const row = (label, value) => {
    const val = (value === null || value === undefined || value === "") ? L.na : String(value);
    doc.setFont("helvetica", "bold"); doc.setFontSize(9); doc.setTextColor(...INK);
    const labelW = 60;
    const wrapped = doc.splitTextToSize(val, W - M - M - labelW);
    ensure(wrapped.length * 4.5 + 2);
    doc.text(`${label}`, M, y);
    doc.setFont("helvetica", "normal"); doc.setTextColor(60, 60, 60);
    doc.text(wrapped, M + labelW, y);
    y += wrapped.length * 4.5 + 1.5;
  };
  const para = (value) => {
    const wrapped = doc.splitTextToSize(String(value || L.na), W - M - M);
    ensure(wrapped.length * 4.5 + 2);
    doc.setFont("helvetica", "normal"); doc.setFontSize(9); doc.setTextColor(60, 60, 60);
    doc.text(wrapped, M, y);
    y += wrapped.length * 4.5 + 1.5;
  };

  // Header
  doc.setFont("helvetica", "bold"); doc.setFontSize(20); doc.setTextColor(...INK);
  doc.text("idevorai", M, y);
  doc.setFont("helvetica", "normal"); doc.setFontSize(8); doc.setTextColor(...MUTE);
  doc.text(`${L.docNo}: ${data.doc_number || "—"}`, W - M, y - 4, { align: "right" });
  doc.text(`${L.generated}: ${fmtUTC(data.generated_at) || "—"}`, W - M, y, { align: "right" });
  y += 8;
  doc.setFont("helvetica", "bold"); doc.setFontSize(14); doc.setTextColor(...INK);
  doc.text(`${L.title}`, M, y);
  doc.setFont("helvetica", "normal"); doc.setFontSize(9); doc.setTextColor(...MUTE);
  doc.text(L.subtitle, M, y + 5);
  y += 10;
  doc.setDrawColor(...ACCENT); doc.setLineWidth(0.6); doc.line(M, y, W - M, y); doc.setLineWidth(0.2);
  y += 6;

  // Payment
  sectionTitle(L.payment);
  row(L.email + ":", data.email);
  row(L.name + ":", data.name);
  row(L.plan + ":", data.plan_name);
  row(L.amount + ":", data.amount != null ? `${Number(data.amount).toFixed(2)} ${data.currency || "EUR"}` : null);
  row(L.paidAt + ":", fmtDual(data.paid_date, L.na));
  row(L.payId + ":", data.whop_payment_id);
  row(L.memId + ":", data.whop_membership_id);
  row(L.method + ":", data.payment_method);
  row(L.status + ":", data.payment_status);
  row(L.account + ":", data.whop_account);

  // Consent
  const c = data.consent || {};
  sectionTitle(L.consent);
  row(L.cgv + ":", `${c.cgv?.accepted ? "[x] " + L.checked : "[ ] " + L.notChecked}  —  ${L.at} ${fmtDual(c.cgv?.at, L.na)}`);
  para(`${L.text}: "${c.cgv?.text || L.na}"`);
  row(L.waiver + ":", `${c.waiver?.accepted ? "[x] " + L.checked : "[ ] " + L.notChecked}  —  ${L.at} ${fmtDual(c.waiver?.at, L.na)}`);
  para(`${L.text}: "${c.waiver?.text || L.na}"`);
  row(L.ip + ":", c.ip);
  row(L.device + ":", c.ua);
  row(L.cgvLink + ":", CGV_URL);

  // Access delivered
  sectionTitle(L.access);
  row(L.accountCreated + ":", fmtDual(data.account_created, L.na));
  row(L.firstBienvenue + ":", fmtDual(data.bienvenue_at, L.na));
  row(L.firstAccess + ":", fmtDual(data.first_access_at, L.na));
  row(L.delay + ":", data.delay_seconds != null ? L.deliveredIn.replace("{n}", data.delay_seconds) : L.na);
  const we = data.welcome_email || {};
  row(L.welcomeEmail + ":", `${we.status === "sent" ? "✓" : (we.status || L.na)}${we.to ? " — " + we.to : ""}`);
  row(L.sentAt + ":", fmtDual(we.sent_at, L.na));

  // Usage
  sectionTitle(L.usage);
  row(L.loginCount + ":", data.login_count ?? 0);
  row(L.timeSpent + ":", fmtDuration(data.time_spent_seconds, L));
  row(L.regens + ":", data.regen_count ?? 0);
  row(L.coach + ":", data.coach_messages ?? 0);
  if (data.businesses?.length) {
    data.businesses.forEach((b) => row(L.businesses + ":", `${b.name || "—"}  (${fmtDual(b.created_at, L.na)})`));
  } else row(L.businesses + ":", L.na);
  doc.setFont("helvetica", "bold"); doc.setFontSize(9); doc.setTextColor(...INK);
  ensure(6);
  const done = (data.missions || []).filter((m) => m.completed);
  doc.text(`${L.logins}:`, M, y); y += 5;
  doc.setFont("helvetica", "normal"); doc.setTextColor(60, 60, 60);
  (data.logins || []).forEach((lg) => {
    ensure(5);
    doc.text(`• ${fmtDual(lg.at, L.na)} — IP ${lg.ip || L.na} — ${(lg.ua || L.na).slice(0, 60)}${lg.kind === "register" ? " (" + L.register + ")" : ""}`, M + 2, y);
    y += 4.5;
  });
  if (!(data.logins || []).length) { ensure(5); doc.text(`• ${L.na}`, M + 2, y); y += 4.5; }

  // Missions
  sectionTitle(L.missions);
  row(L.missions + ":", `${done.length} ${L.missionsDone} / ${(data.missions || []).length} ${L.missionsTotal}`);
  done.slice(0, 20).forEach((m) => { ensure(5); doc.setFont("helvetica", "normal"); doc.setFontSize(8.5); doc.setTextColor(60, 60, 60); doc.text(`• J${m.day} ${m.title || ""} — ${fmtDual(m.at, L.na)}`, M + 2, y); y += 4.3; });

  // Content preview
  sectionTitle(L.preview);
  row(L.businesses + ":", data.preview?.business_name);
  if (data.preview?.excerpt) para(data.preview.excerpt);

  // Integrity: hash + verification QR code
  ensure(34);
  sectionTitle(L.integrity);
  if (qrDataUrl) doc.addImage(qrDataUrl, "PNG", W - M - 26, y - 6, 26, 26);
  doc.setFont("helvetica", "normal"); doc.setFontSize(7); doc.setTextColor(60, 60, 60);
  const hw = doc.splitTextToSize(data.hash || L.na, W - M - M - 32);
  doc.text(hw, M, y);
  y += hw.length * 3.5 + 2;
  if (verifyUrl) {
    doc.setFont("helvetica", "bold"); doc.setFontSize(8); doc.setTextColor(...ACCENT);
    doc.textWithLink(L.verify, M, y, { url: verifyUrl });
    y += 4;
    doc.setFont("helvetica", "normal"); doc.setFontSize(6.5); doc.setTextColor(...MUTE);
    doc.text(L.scan, W - M - 26, y + 2, { maxWidth: 26, align: "left" });
  }
  y += 6;

  // Footer note
  ensure(10);
  y += 4;
  doc.setDrawColor(230, 230, 230); doc.line(M, y, W - M, y); y += 5;
  doc.setFont("helvetica", "italic"); doc.setFontSize(8); doc.setTextColor(...MUTE);
  doc.text(lang === "en" ? "Digital content delivered immediately after payment." : "Contenu numérique livré immédiatement après paiement.", M, y);

  doc.save(`${data.doc_number || "proof"}.pdf`);
}
